// What’s changed in Mor, in the family’s words — newest first, straight off
// `release-notes.json` (`release-notes.ts` fetches it once; see that file and
// `.github/release-notes-prompt.md` for how the entries are written).
//
// Opening this screen is the one thing that clears the account menu’s dot: it records the
// newest entry’s id as seen the moment the list loads, whether or not there is anything new
// to look at. An empty file — nothing has shipped worth telling anyone about yet — says so
// plainly rather than showing nothing at all.

import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { ReleaseNote } from '../api/contract.js';
import { calendarDay } from '../format.js';
import { markReleaseSeen } from '../release-notes-seen.js';
import { releaseNotes } from '../release-notes.js';

export type WhatsNewProps = {
  client: ApiClient;
};

export function WhatsNew({ client }: WhatsNewProps): JSX.Element {
  const [notes, setNotes] = useState<readonly ReleaseNote[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    releaseNotes(client)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setNotes(loaded);
        const newest = loaded[0];
        if (newest !== undefined) {
          markReleaseSeen(newest.id);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProblem('That didn’t load. Try again in a moment.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  return (
    <main className="screen screen--whatsNew">
      <h1 className="screen__title">What’s new</h1>
      {problem === null ? null : (
        <p className="screen__problem" role="status">
          {problem}
        </p>
      )}
      {notes === null ? null : notes.length === 0 ? (
        <p className="screen__empty">Nothing here yet.</p>
      ) : (
        <ul className="releaseNotes__list">
          {notes.map((note) => (
            <li key={note.id} className="releaseNotes__item">
              <p className="releaseNotes__meta">
                {note.version === null
                  ? calendarDay(note.date ?? '')
                  : `Version ${note.version}`}
              </p>
              <h2 className="releaseNotes__title">{note.title}</h2>
              <ul className="releaseNotes__sentences">
                {note.notes.map((sentence) => (
                  <li key={sentence}>{sentence}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
